const EMAIL_LIKE_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const URL_LIKE_REGEX = /^(https?:\/\/|data:image\/|blob:|\/)/i;

const normalizeName = (value) => {
    if (typeof value !== 'string') return '';
    return value.trim().replace(/\s+/g, ' ');
};

const isEmailLike = (value) => EMAIL_LIKE_REGEX.test(value);

const firstToken = (value) => {
    const [first = ''] = value.split(' ');
    return first;
};

const isAvatarLikeUrl = (value) => {
    if (typeof value !== 'string') return false;
    const trimmed = value.trim();
    return URL_LIKE_REGEX.test(trimmed);
};

const getPreferredFullName = (user) => {
    if (!user || typeof user !== 'object') return '';

    const candidates = [
        user.fullName,
        user.displayName,
        user.name,
    ];

    for (const candidate of candidates) {
        const normalized = normalizeName(candidate);
        if (!normalized) continue;
        if (isEmailLike(normalized)) continue;
        return normalized;
    }

    return '';
};

/**
 * Resolve a first name for UI greetings without ever using email values.
 * Priority: firstName -> fullName -> displayName -> name.
 */
export const getUserFirstName = (user) => {
    if (!user || typeof user !== 'object') return '';

    const candidates = [
        user.firstName,
        user.fullName,
        user.displayName,
        user.name,
    ];

    for (const candidate of candidates) {
        const normalized = normalizeName(candidate);
        if (!normalized) continue;
        if (isEmailLike(normalized)) continue;

        const firstName = firstToken(normalized);
        if (firstName && !isEmailLike(firstName)) {
            return firstName;
        }
    }

    return '';
};

export const getUserDisplayName = (user) => {
    const fullName = getPreferredFullName(user);
    if (fullName) return fullName;

    const firstName = normalizeName(user?.firstName || '');
    if (firstName && !isEmailLike(firstName)) return firstName;

    return 'there';
};

export const getUserFullName = (user) => getPreferredFullName(user);

export const getUserInitials = (user) => {
    const fullName = getPreferredFullName(user);
    if (!fullName) {
        const firstName = getUserFirstName(user);
        return firstName ? firstName[0].toUpperCase() : 'A';
    }

    const parts = fullName.split(' ').filter(Boolean);
    if (parts.length === 0) return 'A';
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

export const getUserAvatarUrl = (user) => {
    const candidates = [
        user?.photoURL,
        user?.avatar,
        user?.profileImage,
    ];

    for (const candidate of candidates) {
        if (!isAvatarLikeUrl(candidate)) continue;
        return candidate.trim();
    }

    return '';
};

export const createNormalizedUser = (firebaseUser, dbUser) => {
    if (!firebaseUser && !dbUser) return null;

    const uid = firebaseUser?.uid || dbUser?.uid || dbUser?.id || '';
    const email = normalizeName((dbUser?.email || firebaseUser?.email || '').toLowerCase());

    const fullName = getUserFullName({
        ...firebaseUser,
        ...dbUser,
    });

    const firstName = getUserFirstName({
        ...firebaseUser,
        ...dbUser,
        fullName,
    });

    const avatar = getUserAvatarUrl({
        ...dbUser,
        avatar: dbUser?.avatar || dbUser?.profileImage || '',
    });

    const photoURL = getUserAvatarUrl({
        photoURL: dbUser?.photoURL || firebaseUser?.photoURL || '',
    });

    const normalized = {
        id: uid,
        uid,
        email,
        fullName,
        firstName,
        displayName: getUserDisplayName({ fullName, firstName }),
        photoURL,
        avatar,
    };

    return {
        ...normalized,
        initials: getUserInitials(normalized),
    };
};
